import { CAPABILITY_SCOPES } from '@/auth/scopes';

/**
 * C1 — the transparency screen (§4). Before the admin connects, we name every scope
 * the tool can request and why. This is the zero-egress trust proof made visible.
 * Only read scopes are shown up front; the write scope (C7) is disclosed as opt-in.
 */
export function FirstRunConsent({
  onConnect,
  connecting,
}: {
  onConnect: () => void;
  connecting: boolean;
}) {
  const entries = Object.values(CAPABILITY_SCOPES);
  const reads = entries.filter((e) => e.access === 'read');
  const writes = entries.filter((e) => e.access === 'write');

  return (
    <section className="consent">
      <span className="consent__badge">Zero-egress · delegated · client-side</span>
      <h1 className="consent__h1">Connect your Microsoft 365 tenant</h1>
      <p className="consent__lede">
        Everything runs in your browser using your own delegated admin token. No tenant
        data is sent to any server we operate — there is no server. Permissions are
        requested one module at a time, never all at once.
      </p>

      <h2 className="consent__h2">Read permissions, requested per module</h2>
      <ul className="consent__list">
        {reads.map((e) => (
          <li key={e.capability} className="consent__item">
            <div className="consent__item-head">
              <span className="consent__cap">{e.capability}</span>
              <span className="consent__cap-title">{e.title}</span>
            </div>
            <p className="consent__why">{e.why}</p>
            {e.scopes.length > 0 && (
              <code className="consent__scopes">{e.scopes.join('  ·  ')}</code>
            )}
          </li>
        ))}
      </ul>

      {writes.length > 0 && (
        <>
          <h2 className="consent__h2">Write permission — opt-in only</h2>
          <ul className="consent__list">
            {writes.map((e) => (
              <li key={e.capability} className="consent__item consent__item--write">
                <div className="consent__item-head">
                  <span className="consent__cap">{e.capability}</span>
                  <span className="consent__cap-title">{e.title}</span>
                </div>
                <p className="consent__why">{e.why}</p>
                <code className="consent__scopes">{e.scopes.join('  ·  ')}</code>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="consent__cta">
        <button className="btn btn--primary btn--lg" onClick={onConnect} disabled={connecting}>
          {connecting ? 'Connecting…' : 'Connect & sign in'}
        </button>
      </div>
      <p className="consent__fineprint">
        You will be asked to consent to <strong>{CAPABILITY_SCOPES.C1.title}</strong> only.
        Further scopes are requested when you open each module.
      </p>
    </section>
  );
}
